import { useQuery } from "@tanstack/react-query";
import { HOME } from "../constants/testIds";
import { FEATURED_DROP } from "../constants/business";
import Button from "../components/loadout/Button";
import Reveal from "../components/loadout/Reveal";
import { productByHandle, collectionByHandle } from "../lib/shopify/queries";
import { formatINR, placeholderGradient, firstLetter } from "../lib/product";

// Grace-fallback: if the Z-NEO hub product is missing, use the first product
// of the `frontpage` collection.
async function loadFeatured() {
  try {
    return await productByHandle(FEATURED_DROP.handle);
  } catch {
    try {
      const c = await collectionByHandle("frontpage", { first: 1 });
      const first = c?.products?.[0];
      if (first) return first;
    } catch {}
    return null;
  }
}

// [6] Featured Drop banner — full width 520px min height, dark bg.
export default function FeaturedDrop() {
  const { data: product } = useQuery({
    queryKey: ["shopify", "product", FEATURED_DROP.handle],
    queryFn: loadFeatured,
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const livePrice = product ? formatINR(product.priceRange?.minVariantPrice) : null;
  const displayPrice = livePrice || FEATURED_DROP.fallbackPrice;
  const productImage = product?.featuredImage;

  return (
    <section
      data-testid={HOME.featuredDrop}
      className="relative bg-[#0A0A0A] text-white overflow-hidden"
      style={{ minHeight: 520 }}
    >
      <div
        className="absolute -right-40 top-1/2 -translate-y-1/2 w-[600px] h-[600px] pointer-events-none opacity-30 hidden md:block"
        style={{
          background: "radial-gradient(circle, rgba(255,159,28,0.45) 0%, rgba(255,159,28,0) 60%)",
        }}
      />
      <div className="mx-auto max-w-[1440px] px-6 py-16 md:py-24 grid grid-cols-1 md:grid-cols-[60fr_40fr] gap-10 items-center">
        <div>
          <Reveal>
            <div className="flex items-center gap-3">
              <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#FF9F1C]">
                {FEATURED_DROP.eyebrow}
              </div>
              {FEATURED_DROP.eyebrowMicrocopy && (
                <>
                  <span
                    aria-hidden="true"
                    className="inline-block w-6 h-px bg-[#FF9F1C]/50"
                  />
                  <div
                    data-testid="home-featured-drop-microcopy"
                    className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#FF9F1C]/80"
                  >
                    {FEATURED_DROP.eyebrowMicrocopy}
                  </div>
                </>
              )}
            </div>
          </Reveal>
          <Reveal delay={60}>
            <h2 className="mt-4 font-display text-white text-[36px] md:text-[56px] lg:text-[64px] leading-[0.95]">
              <span
                data-testid="home-featured-drop-headline-1"
                className="block"
              >
                {FEATURED_DROP.headline1}
              </span>
              <span
                data-testid="home-featured-drop-headline-2"
                className="block text-[#FF9F1C]"
              >
                {FEATURED_DROP.headline2}
              </span>
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p
              data-testid="home-featured-drop-subcopy"
              className="mt-6 font-sans text-[15px] md:text-[16px] text-white/85 max-w-[560px] leading-relaxed"
            >
              {FEATURED_DROP.body}
            </p>
          </Reveal>
          <Reveal delay={180}>
            <div className="mt-6 font-sans text-[16px] md:text-[18px] font-medium text-white">
              {product?.title || FEATURED_DROP.productName}
            </div>
          </Reveal>
          <Reveal delay={240}>
            <div
              data-testid={HOME.featuredDropPrice}
              className="mt-2 font-mono text-[22px] md:text-[24px] text-[#FF9F1C] font-semibold"
            >
              {displayPrice}
            </div>
          </Reveal>
          <Reveal delay={300}>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button
                as="a"
                href={FEATURED_DROP.href}
                variant="primary"
                data-testid={HOME.featuredDropCta}
              >
                Configure Yours →
              </Button>
              <Button as="a" href={FEATURED_DROP.href} variant="secondaryDark">
                Read the Brief →
              </Button>
            </div>
          </Reveal>
        </div>

        <div className="hidden md:flex justify-center items-center">
          <div
            className="relative w-full max-w-[420px] aspect-square"
            style={{ filter: "drop-shadow(0 25px 40px rgba(0,0,0,0.6))" }}
          >
            {productImage?.url ? (
              <img
                src={productImage.url}
                alt={productImage.altText || FEATURED_DROP.productName}
                className="w-full h-full object-contain"
              />
            ) : (
              <div
                className="w-full h-full flex items-center justify-center rounded-sm"
                style={{ background: placeholderGradient(FEATURED_DROP.handle) }}
              >
                <span className="font-display text-white/60 text-[140px]">
                  {firstLetter(FEATURED_DROP.productName)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
